import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../api";
import { useT, type Lang } from "../i18n";
import { tg } from "../telegram";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import ClauseReader, { clauseHeading, clauseText, docTitle } from "../components/ClauseReader";
import ClauseTable from "../components/ClauseTable";
import ShareSheet, { type ShareSpec } from "../components/ShareSheet";
import { MIME } from "../share";
import { speak, stopSpeak, ttsSupported, warmUpVoices } from "../speech";
import type { Clause, DocumentFull } from "../types";

const HEADER_KINDS = new Set(["part", "section", "clause"]);
const TTS_ON = ttsSupported();

/** Ordered text blocks (number + heading + text) for a clause subtree, for TTS. */
function collectSpeech(clause: Clause, lang: Lang): string[] {
  const out: string[] = [];
  const walk = (c: Clause) => {
    const parts: string[] = [];
    if (c.number) parts.push(c.number.replace(/\.$/, ""));
    const h = clauseHeading(c, lang);
    if (h) parts.push(h);
    const txt = clauseText(c, lang);
    if (txt) parts.push(txt);
    if (parts.length) out.push(parts.join(". "));
    c.children.forEach(walk);
  };
  walk(clause);
  return out;
}

// ---------- recursive accordion node ----------

interface NodeProps {
  clause: Clause;
  depth: number;
  lang: Lang;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onOpen: (c: Clause) => void;
  onShare: (c: Clause) => void;
  onSpeak: (c: Clause) => void;
  speakingId: string | null;
}

function ClauseNode(props: NodeProps) {
  const { clause, depth, lang, expanded, onToggle, onOpen, onShare, onSpeak, speakingId } = props;
  const { t } = useT();
  const isOpen = expanded.has(clause.id);
  const heading = clauseHeading(clause, lang);
  const text = clauseText(clause, lang);
  const hasTable = !!clause.table;
  const hasChildren = clause.children.length > 0;
  const hasContent = !!text || hasTable;
  const expandable = hasChildren || hasContent;
  const isHeader = HEADER_KINDS.has(clause.kind);
  const snippet = !isHeader && text ? text.replace(/\s+/g, " ").slice(0, 96) : "";
  const indent = 8 + Math.min(depth, 6) * 13;

  return (
    <div className={`clause-node n-${clause.kind || "text"}`}>
      <div
        className="clause-row"
        style={{ paddingInlineStart: indent }}
        onClick={() => expandable && onToggle(clause.id)}
      >
        <span className={"caret" + (isOpen ? " open" : "") + (expandable ? "" : " hidden")}>▸</span>
        {clause.number && <span className="clause-num">{clause.number}</span>}
        <span className="clause-label">
          {heading && <span className="clause-heading">{heading}</span>}
          {snippet && !isOpen && (
            <span className="clause-snippet">
              {heading ? " — " : ""}
              {snippet}
              {text.length > 96 ? "…" : ""}
            </span>
          )}
        </span>
      </div>

      {isOpen && (
        <div className="clause-detail" style={{ paddingInlineStart: indent }}>
          {text && (
            <div className="clause-body">
              {text.split(/\n{2,}/).map((p, i) => (
                <p key={i}>{p.trim()}</p>
              ))}
            </div>
          )}
          {hasTable && <ClauseTable table={clause.table!} lang={lang} />}
          {(hasContent || hasChildren) && (
            <div className="clause-actions">
              {TTS_ON && (
                <button
                  className={"chip" + (speakingId === clause.id ? " speaking" : "")}
                  onClick={(e) => { e.stopPropagation(); onSpeak(clause); }}
                >
                  {speakingId === clause.id ? `⏹ ${t("clause.stop")}` : `🔊 ${t("clause.speak")}`}
                </button>
              )}
              <button className="chip" onClick={(e) => { e.stopPropagation(); onOpen(clause); }}>
                ⤢ {t("clause.open")}
              </button>
              <button className="chip" onClick={(e) => { e.stopPropagation(); onShare(clause); }}>
                🔗 {t("share.button")}
              </button>
            </div>
          )}
          {clause.children.map((ch) => (
            <ClauseNode key={ch.id} {...props} clause={ch} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- page ----------

export default function DocumentView() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [doc, setDoc] = useState<DocumentFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [reader, setReader] = useState<Clause | null>(null);
  const [shareSpec, setShareSpec] = useState<ShareSpec | null>(null);
  const [speakingId, setSpeakingId] = useState<string | null>(null);

  useEffect(() => {
    warmUpVoices();
    return () => stopSpeak();
  }, []);

  useEffect(() => {
    setDoc(null);
    setError(null);
    setExpanded(new Set());
    setReader(null);
    stopSpeak();
    setSpeakingId(null);
    api
      .get<DocumentFull>(`/api/documents/${slug}`)
      .then(setDoc)
      .catch((e: ApiError) => setError(e.message));
  }, [slug]);

  // Stop narration when the language changes (text would no longer match).
  useEffect(() => {
    stopSpeak();
    setSpeakingId(null);
  }, [lang]);

  const onSpeak = useCallback(
    (c: Clause) => {
      if (speakingId === c.id) {
        stopSpeak();
        setSpeakingId(null);
        return;
      }
      tg()?.HapticFeedback?.impactOccurred("light");
      setSpeakingId(c.id);
      speak(collectSpeech(c, lang), lang, () => setSpeakingId(null));
    },
    [speakingId, lang],
  );

  const onBack = useCallback(() => {
    if (reader) {
      setReader(null);
      return;
    }
    navigate("/");
  }, [reader, navigate]);
  useTelegramBackButton(onBack);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    tg()?.HapticFeedback?.selectionChanged?.();
  }, []);

  const allIds = useMemo(() => {
    const ids: string[] = [];
    const walk = (ns: Clause[]) =>
      ns.forEach((n) => {
        if (n.children.length || clauseText(n, lang) || n.table) ids.push(n.id);
        walk(n.children);
      });
    if (doc) walk(doc.clauses);
    return ids;
  }, [doc, lang]);

  const clauseTitle = useCallback(
    (c: Clause) => {
      const h = clauseHeading(c, lang);
      const base = h || clauseText(c, lang).replace(/\s+/g, " ").slice(0, 60);
      return [c.number, base].filter(Boolean).join(" ").trim() || slug;
    },
    [lang, slug],
  );

  const openShareClause = useCallback(
    (c: Clause) => {
      tg()?.HapticFeedback?.impactOccurred("light");
      setShareSpec({
        key: `clause:${c.id}:${lang}`,
        title: clauseTitle(c),
        withFormat: true,
        url: (f) => `/api/documents/${slug}/clauses/${c.id}/export?format=${f}&lang=${lang}`,
        fallbackName: (f) => `${slug}_${c.number || c.id}_${lang}.${f === "pdf" ? "pdf" : "docx"}`,
        mime: (f) => (f === "pdf" ? MIME.pdf : MIME.docx),
      });
    },
    [slug, lang, clauseTitle],
  );

  const openShareDoc = useCallback(() => {
    if (!doc) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setShareSpec({
      key: `doc:${slug}:${lang}`,
      title: docTitle(doc, lang),
      withFormat: true,
      url: (f) => `/api/documents/${slug}/export?format=${f}&lang=${lang}`,
      fallbackName: (f) => `${slug}_${lang}.${f === "pdf" ? "pdf" : "docx"}`,
      mime: (f) => (f === "pdf" ? MIME.pdf : MIME.docx),
    });
  }, [slug, lang, doc]);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q || !doc) return null;
    const out: Clause[] = [];
    const walk = (ns: Clause[]) =>
      ns.forEach((n) => {
        const hay = `${n.number} ${n.heading} ${n.heading_ru || ""} ${n.text_en} ${n.text_ru}`.toLowerCase();
        if (hay.includes(q)) out.push(n);
        walk(n.children);
      });
    walk(doc.clauses);
    return out;
  }, [q, doc]);

  const title = doc ? docTitle(doc, lang) : "";

  return (
    <div className="page">
      <header className="doc-header">
        <button className="back-btn" onClick={() => navigate("/")} aria-label={t("common.back")}>
          ‹
        </button>
        <h1 className="doc-title">{title || slug}</h1>
      </header>

      {!doc && !error && (
        <div className="empty">
          <span className="spinner" />
        </div>
      )}
      {error && <div className="alert error">{error}</div>}

      {doc && (
        <>
          <div className="toolbar">
            <div className="toolbar-group">
              <button className="chip" onClick={openShareDoc}>
                🔗 {t("share.button")}
              </button>
            </div>
            <div className="toolbar-group">
              <button className="chip ghost" onClick={() => setExpanded(new Set(allIds))}>
                {t("doc.expand_all")}
              </button>
              <button className="chip ghost" onClick={() => setExpanded(new Set())}>
                {t("doc.collapse_all")}
              </button>
            </div>
          </div>

          <input
            className="search"
            type="search"
            placeholder={t("doc.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {results ? (
            <div className="results">
              <div className="results-count">{t("doc.results", { n: results.length })}</div>
              {results.length === 0 && <div className="empty">{t("doc.no_results")}</div>}
              {results.map((c) => (
                <button key={c.id} className="result-card" onClick={() => setReader(c)}>
                  {c.number && <span className="clause-num">{c.number}</span>}
                  <span className="result-text">
                    {clauseHeading(c, lang) && <b>{clauseHeading(c, lang)} </b>}
                    {clauseText(c, lang).replace(/\s+/g, " ").slice(0, 120)}…
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="tree">
              {doc.clauses.map((c) => (
                <ClauseNode
                  key={c.id}
                  clause={c}
                  depth={0}
                  lang={lang}
                  expanded={expanded}
                  onToggle={toggle}
                  onOpen={setReader}
                  onShare={openShareClause}
                  onSpeak={onSpeak}
                  speakingId={speakingId}
                />
              ))}
            </div>
          )}
        </>
      )}

      {reader && (
        <ClauseReader
          clause={reader}
          lang={lang}
          onClose={() => setReader(null)}
          onShare={() => openShareClause(reader)}
          onSpeak={() => onSpeak(reader)}
          speaking={speakingId === reader.id}
        />
      )}

      {shareSpec && <ShareSheet spec={shareSpec} onClose={() => setShareSpec(null)} />}
    </div>
  );
}
